import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Sparkles, Loader2, Trash2, Scissors, Save, RotateCcw, CalendarDays, CalendarRange, Calendar, FileText, Zap, Wrench, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { CalendarView } from "./scheduler/CalendarView";
import AssetPreviewPlayer from "@/components/AssetPreviewPlayer";
import BackfillEmptyDialog from "./scheduler/BackfillEmptyDialog";
import CleanupExceptionsDialog from "./scheduler/CleanupExceptionsDialog";
import { CloneToDateDialog } from "./scheduler/CloneToDateDialog";
import { TemplatesDialog } from "./scheduler/TemplatesDialog";
import { GapDetectorDialog } from "./scheduler/GapDetectorDialog";

const DAYS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
const HOUR_HEIGHT = 60; // px per hour (24h = 1440px)
const SLOT_HEIGHT_PER_SEC = HOUR_HEIGHT / 3600;

function fmt(sec) {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function parseTime(hms) {
  const [h, m, s] = hms.split(":").map(Number);
  return h * 3600 + m * 60 + (s || 0);
}

function AutoFillDialog({ open, onClose, channelId, onFilled }) {
  const [hours, setHours] = useState(24);
  const [startDay, setStartDay] = useState(0);
  const [startTime, setStartTime] = useState("06:00:00");
  const [preferredTags, setPreferredTags] = useState("");
  const [clearExisting, setClearExisting] = useState(false);
  const [noRepeatHours, setNoRepeatHours] = useState(24);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setBusy(false); }
  }, [open]);

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/schedule/auto-fill", {
        channel_id: channelId,
        hours: Number(hours),
        start_day_of_week: Number(startDay),
        start_time: startTime,
        preferred_tags: preferredTags.split(",").map((s) => s.trim()).filter(Boolean),
        clear_existing: clearExisting,
        no_repeat_hours: Number(noRepeatHours),
        pattern: ["program", "promo", "ad", "program", "bumper"],
      });
      toast.success(`Added ${r.data.items_added} items · ${(r.data.total_duration_sec / 3600).toFixed(1)}h scheduled`);
      const chan = await api.get("/schedule", { params: { channel_id: channelId } });
      onFilled(chan.data || []);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-fill failed");
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="bg-card border-border rounded-sm max-w-lg" data-testid="autofill-dialog">
        <DialogHeader className="relative pb-4 mb-1">
          <div className="absolute inset-x-0 -top-6 -mx-6 h-1" style={{ background: "var(--brand-gradient)" }} />
          <DialogTitle className="font-display flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-fuchsia-400" strokeWidth={1.75} />
            Smart auto-fill
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground font-mono">
            Populates the schedule with a program → promo → ad → program → bumper loop from your library, ranking assets by preferred tag overlap.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Hours</Label>
              <Input
                type="number" min={1} max={168} value={hours}
                onChange={(e) => setHours(e.target.value)}
                disabled={busy}
                data-testid="autofill-hours"
                className="mt-1 bg-background border-border font-mono text-xs"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Start day</Label>
              <select
                value={startDay}
                onChange={(e) => setStartDay(e.target.value)}
                disabled={busy}
                data-testid="autofill-start-day"
                className="mt-1 w-full h-10 rounded-sm bg-background border border-border font-mono text-xs px-2 uppercase tracking-widest"
              >
                {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d, i) => (
                  <option key={d} value={i}>{d}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Start time</Label>
              <Input
                type="time" step="60" value={startTime.slice(0, 5)}
                onChange={(e) => setStartTime(`${e.target.value}:00`)}
                disabled={busy}
                data-testid="autofill-start-time"
                className="mt-1 bg-background border-border font-mono text-xs"
              />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Preferred tags (comma-separated, optional)</Label>
            <Input
              value={preferredTags}
              onChange={(e) => setPreferredTags(e.target.value)}
              disabled={busy}
              data-testid="autofill-tags"
              placeholder="e.g. drama, cooking, lifestyle, adventure"
              className="mt-1 bg-background border-border font-mono text-xs"
            />
            <div className="text-[10px] text-muted-foreground mt-1 font-mono">
              Assets whose <code>ai_metadata.genre_tags</code> or user tags overlap will rank higher.
            </div>
          </div>
          <label className="flex items-start gap-2 gradient-stroke rounded-xl p-3 cursor-pointer hover:brightness-105" data-testid="autofill-clear-toggle">
            <input
              type="checkbox"
              checked={clearExisting}
              onChange={(e) => setClearExisting(e.target.checked)}
              disabled={busy}
              className="mt-0.5 accent-rose-500"
            />
            <div className="flex-1">
              <div className="font-display font-bold text-sm">Clear existing schedule first</div>
              <div className="text-[10px] font-mono text-muted-foreground mt-0.5">
                Wipes every current schedule item for this channel before filling. Otherwise the new items are appended.
              </div>
            </div>
          </label>
          <div className="gradient-stroke rounded-xl p-3">
            <div className="flex items-center justify-between">
              <div className="font-display font-bold text-sm">No-repeat window</div>
              <div className="flex items-center gap-2">
                <Input
                  type="number" min={0} max={168} step={1}
                  value={noRepeatHours}
                  onChange={(e) => setNoRepeatHours(e.target.value)}
                  disabled={busy}
                  data-testid="autofill-no-repeat-hours"
                  className="w-20 h-8 bg-background border-border font-mono text-xs text-right"
                />
                <span className="text-[11px] font-mono text-muted-foreground">hours</span>
              </div>
            </div>
            <div className="text-[10px] font-mono text-muted-foreground mt-1">
              An asset placed once won&apos;t be placed again within this rolling window — perfect for series so the same episode never airs twice in a day. Set to <code>0</code> to allow repeats.
            </div>
          </div>
        </div>

        <DialogFooter className="pt-4">
          <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-sm border-border">Cancel</Button>
          <Button
            onClick={run}
            disabled={busy || !channelId}
            data-testid="autofill-run-btn"
            className="rounded-sm tenant-btn"
          >
            {busy ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Filling…</> : <><Sparkles className="w-4 h-4 mr-2" /> Fill {hours}h</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Scheduler() {
  const { user } = useAuth();
  const canWrite = user && ["super_admin", "tenant_admin", "scheduler"].includes(user.role);
  const [channels, setChannels] = useState([]);
  const [channelId, setChannelId] = useState("");
  const [items, setItems] = useState([]);
  const [assets, setAssets] = useState([]);
  const [autoFillOpen, setAutoFillOpen] = useState(false);
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [gapOpen, setGapOpen] = useState(false);
  const [dragging, setDragging] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [autoWave, setAutoWave] = useState(false);
  const [viewMode, setViewMode] = useState("week"); // week | month | quarter
  const [rangeAnchor, setRangeAnchor] = useState(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0); return d;
  });
  const [rangeData, setRangeData] = useState({ results: [], loading: false });
  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneTargetDate, setCloneTargetDate] = useState(null);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [templatesInitialDate, setTemplatesInitialDate] = useState(null);
  const [templates, setTemplates] = useState([]);

  const reloadTemplates = async () => {
    if (!channelId) return;
    try {
      const r = await api.get("/schedule-templates", { params: { channel_id: channelId } });
      setTemplates(r.data || []);
    } catch { /* silent */ }
  };
  useEffect(() => { reloadTemplates(); }, [channelId]);

  const [focusId, setFocusId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    api.get("/channels").then((r) => {
      setChannels(r.data);
      const wanted = searchParams.get("channel");
      const match = wanted && r.data.find((c) => c.id === wanted);
      setChannelId(match ? wanted : (r.data[0] ? r.data[0].id : ""));
    });
    api.get("/assets").then((r) => setAssets(r.data));
    // One-shot deep links from the dashboard EPG strip / gap nudge.
    if (searchParams.get("action") === "autofill") setAutoFillOpen(true);
    if (searchParams.get("focus")) setFocusId(searchParams.get("focus"));
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!channelId) return;
    api.get("/schedule", { params: { channel_id: channelId } }).then((r) => setItems(r.data));
  }, [channelId]);

  // Deep-link focus: scroll the targeted slot into view and flash a highlight
  // (from the dashboard EPG "click-to-scrub" action).
  useEffect(() => {
    if (!focusId || !items.length) return;
    setViewMode("week");
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="schedule-item-${focusId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("epg-focus-flash");
        setTimeout(() => el.classList.remove("epg-focus-flash"), 2600);
        const it = items.find((x) => x.id === focusId);
        if (it && it.asset_id) setEditingItem(it);
      }
      // consume the param so it doesn't re-fire on later renders
      setFocusId(null);
      const sp = new URLSearchParams(searchParams);
      sp.delete("focus");
      setSearchParams(sp, { replace: true });
    }, 350);
    return () => clearTimeout(t);
  }, [focusId, items]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Multi-day summary for Month / Quarter views.
  useEffect(() => {
    if (!channelId || viewMode === "week") return;
    const days = viewMode === "quarter" ? 91 : 42;
    // For month view, snap `rangeAnchor` back to the Monday of its ISO week so
    // the visible grid always starts on Monday.
    const anchor = new Date(rangeAnchor);
    if (viewMode === "month") {
      const dow = (anchor.getDay() + 6) % 7; // Mon=0
      const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
      const firstDow = (first.getDay() + 6) % 7;
      first.setDate(first.getDate() - firstDow);
      anchor.setTime(first.getTime());
      // fall through, use `days=42` (6 rows × 7)
    } else if (viewMode === "quarter") {
      const dow = (anchor.getDay() + 6) % 7;
      anchor.setDate(anchor.getDate() - dow);
    }
    const start = anchor.toISOString().slice(0, 10);
    setRangeData((p) => ({ ...p, loading: true }));
    api.get("/schedule/range", { params: { channel_id: channelId, start, days } })
      .then((r) => setRangeData({ results: r.data.results || [], loading: false, start }))
      .catch(() => setRangeData({ results: [], loading: false }));
  }, [channelId, viewMode, rangeAnchor]);

  const grid = useMemo(() => {
    const byDay = Array.from({ length: 7 }, () => []);
    items.forEach((it) => byDay[it.day_of_week].push(it));
    return byDay;
  }, [items]);

  const onDropAsset = async (day, hour) => {
    if (!dragging) return;
    const startSec = hour * 3600;
    const body = {
      channel_id: channelId,
      day_of_week: day,
      start_time: fmt(startSec) + ":00",
      duration_sec: dragging.duration_sec || 1800,
      type: "program",
      asset_id: dragging.id,
      title: dragging.name,
      color: "#3B82F6",
    };
    try {
      const r = await api.post("/schedule", body);
      setItems((p) => [...p, r.data]);
      toast.success(`Added ${dragging.name}`);
      if (r.headers?.["x-auto-published"]) {
        toast.info("Publishing to CDN…", { description: "1 asset queued so today's tile plays from Cloudflare edge" });
      }
    } catch (e) {
      toast.error("Failed to add");
    }
    setDragging(null);
  };

  const del = async (id) => {
    await api.delete(`/schedule/${id}`);
    setItems((p) => p.filter((i) => i.id !== id));
  };

  return (
    <div className="p-4">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">§ Weekly Scheduler</div>
          <h1 className="font-display font-bold text-2xl mt-1">Program grid</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={async () => {
              if (!channelId) return;
              try {
                const r = await api.post("/schedule/tighten", { channel_id: channelId, min_gap_sec: 0 });
                toast.success(`Tightened · moved ${r.data.moved} items · closed ${Math.round(r.data.gaps_closed_sec / 60)} min of dead air${r.data.overlaps_found ? ` · ${r.data.overlaps_found} overlaps left as-is` : ""}`);
                const chan = await api.get("/schedule", { params: { channel_id: channelId } });
                setItems(chan.data || []);
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Tighten failed");
              }
            }}
            disabled={!channelId || !canWrite}
            title={canWrite ? "" : "Read-only role"}
            data-testid="scheduler-tighten-btn"
            variant="outline"
            className="rounded-sm h-10 px-3 gradient-stroke font-display font-bold text-sm"
          >
            <Loader2 className="w-4 h-4 mr-2 hidden" />
            Close gaps
          </Button>
          <Button
            onClick={() => { setTemplatesInitialDate(null); setTemplatesOpen(true); }}
            disabled={!channelId}
            data-testid="scheduler-templates-btn"
            variant="outline"
            className="rounded-sm h-10 px-3 gradient-stroke font-display font-bold text-sm"
          >
            <FileText className="w-4 h-4 mr-2" /> Templates{templates.length ? <span className="ml-1.5 text-[10px] font-mono px-1.5 rounded-sm bg-fuchsia-500/20 text-fuchsia-400">{templates.length}</span> : null}
          </Button>
          <Button
            onClick={() => setBackfillOpen(true)}
            disabled={!canWrite}
            title={canWrite ? "" : "Read-only role"}
            data-testid="scheduler-backfill-btn"
            variant="outline"
            className="rounded-sm h-10 px-3 gradient-stroke font-display font-bold text-sm"
          >
            <Zap className="w-4 h-4 mr-2" /> Fill empty blocks
          </Button>
          <Button
            onClick={() => setCleanupOpen(true)}
            disabled={!canWrite}
            title={canWrite ? "" : "Read-only role"}
            data-testid="scheduler-cleanup-btn"
            variant="outline"
            className="rounded-sm h-10 px-3 gradient-stroke font-display font-bold text-sm"
          >
            <Wrench className="w-4 h-4 mr-2" /> Cleanup
          </Button>
          <Button
            onClick={() => setGapOpen(true)}
            data-testid="scheduler-gaps-btn"
            variant="outline"
            className="rounded-sm h-10 px-3 gradient-stroke font-display font-bold text-sm"
          >
            <AlertTriangle className="w-4 h-4 mr-2" /> Gap detector
          </Button>
          <Button
            onClick={() => setAutoFillOpen(true)}
            disabled={!channelId || !canWrite}
            title={canWrite ? "" : "Read-only role"}
            data-testid="scheduler-autofill-btn"
            className="tenant-btn rounded-sm h-10 px-4 font-display font-bold text-sm"
          >
            <Sparkles className="w-4 h-4 mr-2" /> Smart auto-fill
          </Button>
          <Select value={channelId} onValueChange={setChannelId}>
            <SelectTrigger className="w-56 bg-card border-border rounded-sm" data-testid="scheduler-channel-select">
              <SelectValue placeholder="Channel" />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              {channels.map((c) => (
                <SelectItem key={c.id} value={c.id} data-testid={`scheduler-channel-opt-${c.slug}`}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="ml-2 inline-flex rounded-sm border border-border overflow-hidden" data-testid="view-mode-switch">
            {[
              { k: "week", label: "Week", Icon: CalendarDays },
              { k: "month", label: "Month", Icon: Calendar },
              { k: "quarter", label: "Quarter", Icon: CalendarRange },
            ].map(({ k, label, Icon }) => (
              <button
                key={k}
                onClick={() => setViewMode(k)}
                data-testid={`view-mode-${k}`}
                className={`px-2.5 py-1.5 text-[11px] font-mono uppercase tracking-widest flex items-center gap-1 ${
                  viewMode === k ? "bg-fuchsia-500/15 text-fuchsia-500" : "hover:bg-muted/40 text-muted-foreground"
                }`}
              >
                <Icon className="w-3 h-3" /> {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <AutoFillDialog
        open={autoFillOpen}
        onClose={() => setAutoFillOpen(false)}
        channelId={channelId}
        onFilled={(fresh) => setItems(fresh)}
      />

      <BackfillEmptyDialog
        open={backfillOpen}
        onClose={() => setBackfillOpen(false)}
        channels={channels}
      />

      <GapDetectorDialog
        open={gapOpen}
        onClose={() => setGapOpen(false)}
        onJumpToChannel={(cid) => { setChannelId(cid); setViewMode("week"); setGapOpen(false); }}
      />

      <CleanupExceptionsDialog
        open={cleanupOpen}
        onClose={() => setCleanupOpen(false)}
        channelId={channelId}
        channelName={channels.find((c) => c.id === channelId)?.name}
        onDone={async () => {
          if (!channelId) return;
          const chan = await api.get("/schedule", { params: { channel_id: channelId } });
          setItems(chan.data || []);
        }}
      />

      {viewMode !== "week" && (
        <CalendarView
          mode={viewMode}
          rangeAnchor={rangeAnchor}
          setRangeAnchor={setRangeAnchor}
          data={rangeData}
          onOpenDay={(dateIso) => {
            const [y, m, d] = dateIso.split("-").map(Number);
            setRangeAnchor(new Date(y, m - 1, d));
            setViewMode("week");
          }}
          onCloneToDate={(dateIso) => { setCloneTargetDate(dateIso); setCloneOpen(true); }}
          onApplyTemplate={(dateIso) => { setTemplatesInitialDate(dateIso); setTemplatesOpen(true); }}
          onClearException={async (dateIso) => {
            try {
              await api.delete(`/schedule/exceptions/${channelId}/${dateIso}`);
              toast.success(`Reverted ${dateIso} to weekly template`);
              setRangeAnchor(new Date(rangeAnchor.getTime()));
            } catch (e) { toast.error(e?.response?.data?.detail || "Revert failed"); }
          }}
        />
      )}

      <CloneToDateDialog
        open={cloneOpen}
        targetDate={cloneTargetDate}
        channelId={channelId}
        onClose={() => setCloneOpen(false)}
        onCloned={() => setRangeAnchor(new Date(rangeAnchor.getTime()))}
      />

      <TemplatesDialog
        open={templatesOpen}
        onClose={() => setTemplatesOpen(false)}
        channelId={channelId}
        channels={channels}
        templates={templates}
        initialTargetDate={templatesInitialDate}
        canWrite={canWrite}
        onChange={async () => {
          await reloadTemplates();
          setRangeAnchor(new Date(rangeAnchor.getTime()));
        }}
      />

      {viewMode === "week" && (
      <div className="grid grid-cols-[220px_1fr] gap-4">
        {/* Asset palette */}
        <div className="panel h-[calc(100vh-180px)] flex flex-col">
          <div className="panel-header"><span className="panel-title">Programs</span></div>
          <div className="p-2 overflow-y-auto scrollbar-thin flex-1">
            {assets.filter((a) => a.type === "program").map((a) => (
              <div
                key={a.id}
                draggable
                onDragStart={() => setDragging(a)}
                onDragEnd={() => setDragging(null)}
                data-testid={`asset-drag-${a.id}`}
                className="px-2 py-2 mb-1 rounded-sm border border-zinc-800 bg-zinc-950 hover:border-zinc-600 cursor-grab active:cursor-grabbing"
              >
                <div className="text-xs truncate">{a.name}</div>
                <div className="text-[10px] font-mono text-zinc-500">
                  {Math.floor(a.duration_sec / 60)}m {Math.round(a.duration_sec % 60)}s
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Week grid */}
        <div className="panel overflow-hidden">
          <div className="panel-header">
            <span className="panel-title">Week timeline</span>
            <span className="text-[10px] font-mono text-zinc-500">Drag programs from left onto the grid</span>
          </div>
          <div className="overflow-auto scrollbar-thin" style={{ maxHeight: "calc(100vh - 220px)" }}>
            <div className="grid grid-cols-[48px_repeat(7,1fr)] min-w-[900px]">
              <div />
              {DAYS.map((d) => (
                <div key={d} className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 text-center py-2 border-b border-zinc-800">
                  {d}
                </div>
              ))}

              {/* Time column */}
              <div className="border-r border-zinc-800">
                {Array.from({ length: 24 }, (_, h) => (
                  <div key={h} style={{ height: HOUR_HEIGHT }} className="text-[10px] font-mono text-zinc-500 pr-2 text-right border-b border-zinc-900">
                    {String(h).padStart(2, "0")}:00
                  </div>
                ))}
              </div>

              {/* Day columns */}
              {DAYS.map((_, dayIdx) => (
                <div key={dayIdx} className="relative border-r border-zinc-900">
                  {Array.from({ length: 24 }, (_, h) => (
                    <div
                      key={h}
                      style={{ height: HOUR_HEIGHT }}
                      className="border-b border-zinc-900 hover:bg-zinc-900/40"
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => onDropAsset(dayIdx, h)}
                      data-testid={`grid-cell-${dayIdx}-${h}`}
                    />
                  ))}
                  {/* Scheduled items */}
                  {grid[dayIdx].map((it) => {
                    const s = parseTime(it.start_time);
                    const top = s * SLOT_HEIGHT_PER_SEC;
                    const height = Math.max(18, it.duration_sec * SLOT_HEIGHT_PER_SEC);
                    const bg = it.type === "ad_break" ? "#78350F" : "#15803D";
                    const border = it.type === "ad_break" ? "#F59E0B" : "#4ADE80";
                    const hasOverride = it.trim_in_sec != null || it.trim_out_sec != null;
                    return (
                      <div
                        key={it.id}
                        onClick={() => { if (it.asset_id) { setAutoWave(false); setEditingItem(it); } }}
                        onContextMenu={(e) => {
                          if (it.asset_id) { e.preventDefault(); setAutoWave(true); setEditingItem(it); }
                        }}
                        className={`absolute left-0.5 right-0.5 rounded-sm px-1.5 py-0.5 text-[10px] overflow-hidden group ${it.asset_id ? "z-20 cursor-pointer hover:ring-1 hover:ring-fuchsia-400" : "z-10 cursor-default"}`}
                        style={{
                          top, height, background: bg,
                          borderLeft: `2px solid ${hasOverride ? "#D946EF" : border}`,
                          boxShadow: hasOverride ? "0 0 0 1px rgba(217,70,239,0.35) inset" : undefined,
                        }}
                        title={`${it.title} @ ${it.start_time}${hasOverride ? " · slot trim override active" : ""}${it.asset_id ? " · click to edit slot trim · right-click → waveform editor" : ""}`}
                        data-testid={`schedule-item-${it.id}`}
                      >
                        <div className="flex items-center justify-between gap-1">
                          <span className="font-mono truncate flex items-center gap-1">
                            {hasOverride && <Scissors className="w-2.5 h-2.5 text-fuchsia-300 shrink-0" />}
                            {it.start_time.slice(0, 5)} · {it.title}
                          </span>
                          <button
                            onClick={(e) => { e.stopPropagation(); del(it.id); }}
                            className="opacity-0 group-hover:opacity-100 hover:text-red-400"
                            data-testid={`schedule-del-${it.id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      )}
      <SlotTrimDialog
        item={editingItem}
        assets={assets}
        autoWave={autoWave}
        onClose={() => { setEditingItem(null); setAutoWave(false); }}
        onSaved={(updated) => {
          setItems((p) => p.map((i) => (i.id === updated.id ? { ...i, ...updated } : i)));
          setEditingItem(null);
          setAutoWave(false);
        }}
      />
    </div>
  );
}

function fmtClock(sec) {
  const s = Math.max(0, Number(sec) || 0);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s - h * 3600 - m * 60;
  const whole = Math.floor(r);
  const ms = Math.round((r - whole) * 1000);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(whole).padStart(2, "0")}.${String(ms).padStart(3, "0")}`;
}
function parseClock(str) {
  if (str == null || !String(str).trim()) return null;
  const parts = String(str).trim().split(":");
  let h = 0, m = 0, r = 0;
  if (parts.length === 3)      { h = +parts[0]; m = +parts[1]; r = +parts[2]; }
  else if (parts.length === 2) { m = +parts[0]; r = +parts[1]; }
  else                          { r = +parts[0]; }
  if ([h, m, r].some((n) => !Number.isFinite(n))) return NaN;
  return h * 3600 + m * 60 + r;
}

function SlotTrimDialog({ item, assets, autoWave, onClose, onSaved }) {
  const [tIn, setTIn] = useState("");
  const [tOut, setTOut] = useState("");
  const [saving, setSaving] = useState(false);
  const [showWave, setShowWave] = useState(false);
  const [fullAsset, setFullAsset] = useState(null);
  const [loadingWave, setLoadingWave] = useState(false);

  const asset = useMemo(() => assets.find((a) => a.id === item?.asset_id) || null, [item, assets]);

  useEffect(() => {
    if (!item) return;
    setTIn(item.trim_in_sec != null ? fmtClock(item.trim_in_sec) : "");
    setTOut(item.trim_out_sec != null ? fmtClock(item.trim_out_sec) : "");
  }, [item]);

  // Right-click on a slot tile jumps straight into the waveform editor.
  useEffect(() => {
    if (!item || !autoWave || !item.asset_id) return;
    let cancelled = false;
    (async () => {
      setLoadingWave(true);
      try {
        const r = await api.get(`/assets/${item.asset_id}`);
        if (!cancelled) { setFullAsset(r.data); setShowWave(true); }
      } catch {
        toast.error("Could not load asset for the waveform editor");
      }
      if (!cancelled) setLoadingWave(false);
    })();
    return () => { cancelled = true; };
  }, [item, autoWave]);

  if (!item) return null;

  const assetDur = Number(asset?.duration_sec) || 0;
  const globalIn = asset?.trim_in_sec;
  const globalOut = asset?.trim_out_sec;

  const save = async () => {
    const inSec = parseClock(tIn);
    const outSec = parseClock(tOut);
    if ((tIn && !Number.isFinite(inSec)) || (tOut && !Number.isFinite(outSec))) {
      toast.error("Use HH:MM:SS.ms format"); return;
    }
    if (inSec != null && (inSec < 0 || (assetDur && inSec >= assetDur))) {
      toast.error(`IN must be between 00:00:00 and ${fmtClock(assetDur)}`); return;
    }
    if (outSec != null && (outSec <= 0 || (assetDur && outSec > assetDur))) {
      toast.error(`OUT must be between 00:00:00 and ${fmtClock(assetDur)}`); return;
    }
    if (inSec != null && outSec != null && outSec <= inSec) {
      toast.error("OUT must be greater than IN"); return;
    }
    setSaving(true);
    try {
      const r = await api.patch(`/schedule/${item.id}`, {
        trim_in_sec: inSec ?? null,
        trim_out_sec: outSec ?? null,
      });
      toast.success((inSec == null && outSec == null) ? "Slot override cleared" : "Slot trim override saved");
      if (r.headers?.["x-auto-published"]) {
        toast.info("Publishing to CDN…", { description: "1 asset queued so today's tile plays from Cloudflare edge" });
      }
      onSaved(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save override");
    }
    setSaving(false);
  };

  const clear = () => { setTIn(""); setTOut(""); };
  const useGlobal = () => {
    if (globalIn != null) setTIn(fmtClock(globalIn));
    if (globalOut != null) setTOut(fmtClock(globalOut));
  };

  const openWaveEditor = async () => {
    if (!item?.asset_id) return;
    setLoadingWave(true);
    try {
      const r = await api.get(`/assets/${item.asset_id}`);
      setFullAsset(r.data);
      setShowWave(true);
    } catch {
      toast.error("Could not load asset for the waveform editor");
    }
    setLoadingWave(false);
  };

  const saveOverrideFromWave = async (inSec, outSec) => {
    const r = await api.patch(`/schedule/${item.id}`, {
      trim_in_sec: inSec ?? null,
      trim_out_sec: outSec ?? null,
    });
    setTIn(inSec != null ? fmtClock(inSec) : "");
    setTOut(outSec != null ? fmtClock(outSec) : "");
    toast.success((inSec == null && outSec == null) ? "Slot override cleared" : "Slot trim override saved");
    if (r.headers?.["x-auto-published"]) {
      toast.info("Publishing to CDN…", { description: "1 asset queued so today's tile plays from Cloudflare edge" });
    }
    onSaved(r.data);
    setShowWave(false);
  };

  const fps = Number(asset?.fps) > 0 ? Number(asset.fps) : 25;
  const frameSec = 1 / fps;
  const nudgeField = (which, delta) => {
    const cur = which === "in" ? parseClock(tIn) : parseClock(tOut);
    let base = Number.isFinite(cur) ? cur : (which === "in" ? 0 : (assetDur || 0));
    let t = base + delta;
    t = Math.max(0, assetDur ? Math.min(t, assetDur) : t);
    (which === "in" ? setTIn : setTOut)(fmtClock(t));
  };
  const NudgeRow = ({ which }) => (
    <div className="flex items-center gap-1 mt-1" data-testid={`slot-trim-${which}-nudge`}>
      <Button variant="outline" size="sm" className="rounded-sm h-6 px-1.5 text-[10px]" onClick={() => nudgeField(which, -1)} data-testid={`slot-trim-${which}-minus-1s`} title={`${which.toUpperCase()} −1s`}>−1s</Button>
      <Button variant="outline" size="sm" className="rounded-sm h-6 px-1.5 text-[10px]" onClick={() => nudgeField(which, -frameSec)} data-testid={`slot-trim-${which}-minus-frame`} title={`${which.toUpperCase()} −1 frame`}>−1f</Button>
      <Button variant="outline" size="sm" className="rounded-sm h-6 px-1.5 text-[10px]" onClick={() => nudgeField(which, frameSec)} data-testid={`slot-trim-${which}-plus-frame`} title={`${which.toUpperCase()} +1 frame`}>+1f</Button>
      <Button variant="outline" size="sm" className="rounded-sm h-6 px-1.5 text-[10px]" onClick={() => nudgeField(which, 1)} data-testid={`slot-trim-${which}-plus-1s`} title={`${which.toUpperCase()} +1s`}>+1s</Button>
    </div>
  );

  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-card border-border rounded-sm max-w-lg" data-testid="slot-trim-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Scissors className="w-4 h-4 text-fuchsia-400" strokeWidth={1.75} /> Slot trim override
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Override the asset&apos;s global trim <b>just for this slot</b>. Leave both blank to fall back to the asset&apos;s trim (or full length).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <div className="rounded-sm bg-muted/40 border border-border px-3 py-2 space-y-1">
            <div className="font-display font-bold">{item.title}</div>
            <div className="text-[11px] font-mono text-muted-foreground">
              {item.start_time} · day {item.day_of_week} · duration {fmtClock(item.duration_sec)}
            </div>
            {asset ? (
              <div className="text-[11px] font-mono text-muted-foreground">
                Asset: {asset.name} · raw {fmtClock(assetDur)}
                {(globalIn != null || globalOut != null) && (
                  <> · <span className="text-fuchsia-400">global trim {globalIn != null ? fmtClock(globalIn) : "—"} → {globalOut != null ? fmtClock(globalOut) : "—"}</span></>
                )}
              </div>
            ) : (
              <div className="text-[11px] font-mono text-amber-400">Asset not found in the current library</div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Slot IN override</Label>
              <Input
                className="rounded-sm mt-1 font-mono text-xs h-8"
                placeholder="00:00:00.000"
                value={tIn}
                onChange={(e) => setTIn(e.target.value)}
                data-testid="slot-trim-in"
              />
              <NudgeRow which="in" />
            </div>
            <div>
              <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Slot OUT override</Label>
              <Input
                className="rounded-sm mt-1 font-mono text-xs h-8"
                placeholder={fmtClock(assetDur)}
                value={tOut}
                onChange={(e) => setTOut(e.target.value)}
                data-testid="slot-trim-out"
              />
              <NudgeRow which="out" />
            </div>
          </div>

          {(globalIn != null || globalOut != null) && (
            <Button
              variant="outline"
              size="sm"
              onClick={useGlobal}
              className="rounded-sm text-xs h-8"
              data-testid="slot-trim-copy-global"
              title="Copy asset's global trim as a starting point"
            >
              <RotateCcw className="w-3 h-3 mr-1" /> Copy from asset global
            </Button>
          )}
          {asset && (
            <Button
              variant="outline"
              size="sm"
              onClick={openWaveEditor}
              disabled={loadingWave}
              className="rounded-sm text-xs h-8 w-full border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10"
              data-testid="slot-trim-open-waveform"
              title="Frame-accurate waveform editor with scene/silence snap for this slot"
            >
              {loadingWave ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Scissors className="w-3 h-3 mr-1" />}
              Waveform editor (frame-accurate)
            </Button>
          )}
        </div>

        {fullAsset && (
          <AssetPreviewPlayer
            asset={fullAsset}
            open={showWave}
            onClose={() => setShowWave(false)}
            overrideMode
            overrideIn={parseClock(tIn) ?? item.trim_in_sec ?? asset?.trim_in_sec ?? null}
            overrideOut={parseClock(tOut) ?? item.trim_out_sec ?? asset?.trim_out_sec ?? null}
            onSaveOverride={saveOverrideFromWave}
          />
        )}
        <DialogFooter className="gap-1">
          <Button
            variant="outline"
            size="sm"
            className="rounded-sm h-8 text-xs border-rose-500/40 text-rose-500 hover:bg-rose-500/10"
            onClick={clear}
            data-testid="slot-trim-clear"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Clear override
          </Button>
          <Button
            size="sm"
            className="rounded-sm h-8 text-xs tenant-btn"
            onClick={save}
            disabled={saving}
            data-testid="slot-trim-save"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
            Save override
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
